import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';

// Client-side validation, defence in depth. Sign-in accepts any length so
// accounts made under the old 6-character minimum still work; new passwords
// need NEW_PASSWORD_MIN.
export const NEW_PASSWORD_MIN = 8;
const emailSchema = z.string().trim().toLowerCase().email().max(255);
const signInSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(100),
});
const signUpSchema = z.object({
  email: emailSchema,
  password: z.string().min(NEW_PASSWORD_MIN).max(100),
});

// "Remember me" used to live here: unticked, it wiped the session on every
// reload and tab close, and since the session is shared with fernduft.com it
// signed people out of the website too. Everyone now stays signed in until
// they sign out; the leftover flag is removed on load.
const LEGACY_REMEMBER_ME_KEY = 'joyfume.auth.rememberMe';

// Every call that Supabase's bot protection guards takes a `captcha` source
// (useTurnstile's `take`, src/lib/turnstile.ts) and sends its token as
// `options.captchaToken`. With bot protection off it yields undefined.
export type CaptchaSource = () => Promise<string | undefined>;
const noCaptcha: CaptchaSource = async () => undefined;
/** Message of the error returned when the bot check could not run at all. */
export const CAPTCHA_UNAVAILABLE = 'captcha_unavailable';

async function captchaToken(captcha: CaptchaSource): Promise<{ token: string | undefined } | null> {
  try {
    return { token: await captcha() };
  } catch {
    return null;
  }
}

// Kit orders bought through Stripe Payment Links are attached to the account
// whose confirmed email matches the email used at Stripe. The database does
// the matching and refuses unconfirmed addresses; this only asks it to, once
// per user per page load. Deferred out of the auth callback, where awaiting
// another supabase call can deadlock supabase-js.
// Spec: website docs/specs/ORDER_ACCOUNT_LINKING.md.
const claimedFor = new Set<string>();
function claimOrders(userId: string) {
  if (claimedFor.has(userId)) return;
  claimedFor.add(userId);
  setTimeout(() => {
    supabase.rpc('claim_my_orders').then(({ error }) => {
      if (error) console.warn('claim_my_orders failed:', error.message);
    });
  }, 0);
}

interface UpdateProfilePayload {
  email?: string;
  password?: string;
  display_name?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string, captcha?: CaptchaSource) => Promise<{ error: Error | null }>;
  /** `needsConfirmation`: the address must be confirmed by email before the first sign-in. */
  signUp: (
    email: string,
    password: string,
    captcha?: CaptchaSource,
  ) => Promise<{ error: Error | null; needsConfirmation: boolean }>;
  /** Emails a one-time code (and link); creates the account if the address is new. */
  sendEmailCode: (email: string, captcha?: CaptchaSource) => Promise<{ error: Error | null }>;
  verifyEmailCode: (email: string, code: string, captcha?: CaptchaSource) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<{ error: Error | null }>;
  resetPassword: (email: string, captcha?: CaptchaSource) => Promise<{ error: Error | null }>;
  updateProfile: (payload: UpdateProfilePayload) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        if (event === 'SIGNED_IN' && session?.user) claimOrders(session.user.id);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    try {
      localStorage.removeItem(LEGACY_REMEMBER_ME_KEY);
    } catch {
      /* ignore */
    }

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string, captcha = noCaptcha) => {
    const validation = signInSchema.safeParse({ email, password });
    if (!validation.success) {
      return { error: new Error('Invalid email or password format') };
    }
    const c = await captchaToken(captcha);
    if (!c) return { error: new Error(CAPTCHA_UNAVAILABLE) };
    const { error } = await supabase.auth.signInWithPassword({
      email: validation.data.email,
      password: validation.data.password,
      options: { captchaToken: c.token },
    });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, captcha = noCaptcha) => {
    const validation = signUpSchema.safeParse({ email, password });
    if (!validation.success) {
      return {
        error: new Error(`Use a valid email and a password of at least ${NEW_PASSWORD_MIN} characters`),
        needsConfirmation: false,
      };
    }
    const c = await captchaToken(captcha);
    if (!c) return { error: new Error(CAPTCHA_UNAVAILABLE), needsConfirmation: false };
    const { data, error } = await supabase.auth.signUp({
      email: validation.data.email,
      password: validation.data.password,
      options: { emailRedirectTo: `${window.location.origin}/`, captchaToken: c.token },
    });
    // No session = confirm the address first. Supabase answers the same way
    // for an address that already has an account, so nobody can probe which
    // addresses are registered.
    return { error: error as Error | null, needsConfirmation: !error && !data.session };
  };

  const sendEmailCode = async (email: string, captcha = noCaptcha) => {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) return { error: new Error('Please enter a valid email address') };
    const c = await captchaToken(captcha);
    if (!c) return { error: new Error(CAPTCHA_UNAVAILABLE) };
    const { error } = await supabase.auth.signInWithOtp({
      email: parsed.data,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: `${window.location.origin}/`,
        captchaToken: c.token,
      },
    });
    return { error: error as Error | null };
  };

  const verifyEmailCode = async (email: string, code: string, captcha = noCaptcha) => {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) return { error: new Error('Please enter a valid email address') };
    const token = code.replace(/\s/g, '');
    // "email" covers sign-in codes; a sign-up confirmation code is "signup"
    // on older auth servers. Bot-check tokens are single-use, so the second
    // try asks for a new one.
    const c1 = await captchaToken(captcha);
    if (!c1) return { error: new Error(CAPTCHA_UNAVAILABLE) };
    const first = await supabase.auth.verifyOtp({
      email: parsed.data,
      token,
      type: 'email',
      options: { captchaToken: c1.token },
    });
    if (!first.error) return { error: null };
    const c2 = await captchaToken(captcha);
    if (!c2) return { error: first.error as Error };
    const second = await supabase.auth.verifyOtp({
      email: parsed.data,
      token,
      type: 'signup',
      options: { captchaToken: c2.token },
    });
    return { error: second.error ? (first.error as Error) : null };
  };

  // "local", not the default "global": signing out here must not sign the
  // same person out on their phone and every other browser.
  const signOut = async () => {
    await supabase.auth.signOut({ scope: 'local' });
  };

  const resetPassword = async (email: string, captcha = noCaptcha) => {
    const parsed = z.string().trim().email().max(255).safeParse(email);
    if (!parsed.success) {
      return { error: new Error('Please enter a valid email address') };
    }
    const redirectTo = `${window.location.origin}/reset-password`;
    const c = await captchaToken(captcha);
    if (!c) return { error: new Error(CAPTCHA_UNAVAILABLE) };
    const { error } = await supabase.auth.resetPasswordForEmail(parsed.data, {
      redirectTo,
      captchaToken: c.token,
    });
    return { error: error as Error | null };
  };

  const updateProfile = async ({ email, password, display_name }: UpdateProfilePayload) => {
    if (!user) return { error: new Error('Not signed in') };

    // Update auth fields first (email / password)
    const authPayload: { email?: string; password?: string } = {};
    if (email && email !== user.email) {
      const parsed = z.string().trim().email().max(255).safeParse(email);
      if (!parsed.success) return { error: new Error('Invalid email address') };
      authPayload.email = parsed.data;
    }
    if (password) {
      const parsed = z.string().min(NEW_PASSWORD_MIN).max(100).safeParse(password);
      if (!parsed.success) return { error: new Error(`Password must be ${NEW_PASSWORD_MIN}-100 characters`) };
      authPayload.password = parsed.data;
    }
    if (authPayload.email || authPayload.password) {
      const { error } = await supabase.auth.updateUser(authPayload);
      if (error) return { error: error as Error };
    }

    // Update profile row (display_name)
    if (typeof display_name === 'string') {
      const parsed = z.string().trim().max(80).safeParse(display_name);
      if (!parsed.success) return { error: new Error('Display name too long') };
      const { error } = await supabase
        .from('profiles')
        .upsert(
          { user_id: user.id, display_name: parsed.data || null },
          { onConflict: 'user_id' }
        );
      if (error) return { error: error as Error };
    }

    return { error: null };
  };

  const deleteAccount = async () => {
    if (!user || !session) {
      return { error: new Error('No user logged in') };
    }

    try {
      const { error } = await supabase.functions.invoke('delete-user', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        return { error: new Error(error.message || 'Failed to delete account') };
      }

      await supabase.auth.signOut({ scope: 'local' });

      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signIn,
        signUp,
        sendEmailCode,
        verifyEmailCode,
        signOut,
        deleteAccount,
        resetPassword,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
